"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getApplicationDataAfterStakeAction,
  getManagedValidatorClaimTxAction,
  getManagedValidatorFinancialCardsAction,
  getManagedValidatorUnstakeTxAction,
} from "@/resources/actions/user.actions";
import type { GetApplicationDataAfterStakeRequest } from "@/resources/interfaces/user.interface";
import { queryKeys } from "@/lib/query-keys";

/**
 * Query hook — fetch managed-validator application data after stake tx confirmation.
 * Uses TanStack Query cache via fetchQuery (imperative query execution).
 */
export function useGetApplicationDataAfterStake() {
  const queryClient = useQueryClient();

  return async (params: GetApplicationDataAfterStakeRequest) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.validators.applications.applicationDataAfterStake(
        params.application_id,
        params.wallet,
      ),
      queryFn: async () => {
        const result = await getApplicationDataAfterStakeAction(params);
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: 0,
    });
}

/**
 * Query hook — fetch managed-validator financial cards data.
 * Declarative query hook using TanStack useQuery.
 */
export function useGetManagedValidatorFinancialCards(
  params: GetApplicationDataAfterStakeRequest,
) {
  return useQuery({
    queryKey: queryKeys.validators.applications.managedFinancialCards(
      params.application_id,
      params.wallet,
    ),
    queryFn: async () => {
      const result = await getManagedValidatorFinancialCardsAction(params);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: Boolean(params.application_id && params.wallet),
    staleTime: 0,
  });
}

/**
 * Query hook — fetch managed-validator unstake tx data.
 * Uses TanStack Query cache via fetchQuery (imperative query execution).
 */
export function useGetManagedValidatorUnstakeTx() {
  const queryClient = useQueryClient();

  return async (params: GetApplicationDataAfterStakeRequest) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.validators.applications.managedUnstakeTx(
        params.application_id,
        params.wallet,
      ),
      queryFn: async () => {
        const result = await getManagedValidatorUnstakeTxAction(params);
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: 0,
    });
}

/**
 * Query hook — fetch managed-validator claim tx data.
 * Uses TanStack Query cache via fetchQuery (imperative query execution).
 */
export function useGetManagedValidatorClaimTx() {
  const queryClient = useQueryClient();

  return async (params: GetApplicationDataAfterStakeRequest) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.validators.applications.managedClaimTx(
        params.application_id,
        params.wallet,
      ),
      queryFn: async () => {
        const result = await getManagedValidatorClaimTxAction(params);
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: 0,
    });
}
